import React from "react";
import termsRaw from "../legal/TERMS.md?raw";
import privacyRaw from "../legal/PRIVACY.md?raw";
import "./ui.css";

/**
 * Anivaze — landing page (Persuade surface). The claim IS the demo: everything
 * described here is live behind the CTA. Chrome-dark, one accent, real
 * hierarchy — no gradient text, no card soup (impeccable craft floor).
 * Routes (index.tsx): "" → Landing · #app → studio · #terms/#privacy → legal.
 */

const ACCENT = "#ff5d33";

export const Landing: React.FC<{ onStart: () => void }> = ({ onStart }) => (
  <div style={L.root}>
    <header style={L.nav}>
      <div style={L.brand}><span style={L.dot} /> Anivaze</div>
      <nav style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <a href="#pricing" style={L.navLink}>Pricing</a>
        <button className="v4-btn-primary" style={L.navCta} onClick={onStart}>Open the studio</button>
      </nav>
    </header>

    {/* ── Hero ── */}
    <section style={L.hero}>
      <h1 style={L.h1}>Type a topic.<br />Get a <em style={L.h1em}>taught</em> video.</h1>
      <p style={L.sub}>
        Anivaze plans the script, acts it out with real animation, narrates it in a studio voice,
        and cuts it like an editor — a finished learning video in about a minute.
      </p>
      <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
        <button className="v4-btn-primary" style={L.bigCta} onClick={onStart}>🎬 Make your free video</button>
        <a href="#how" style={{ ...L.navLink, alignSelf: "center" }}>How it works ↓</a>
      </div>
      <p style={L.heroNote}>One free video per person — 2 minutes, full quality, no signup.</p>
    </section>

    {/* ── Proof strip ── */}
    <section style={L.strip}>
      {["8 animation themes", "12 narrator voices", "6 languages", "7 art styles", "Green-screen exports", "Publish to YouTube"].map((s) => (
        <span key={s} style={L.stripItem}>{s}</span>
      ))}
    </section>

    {/* ── How it works ── */}
    <section id="how" style={L.section}>
      <h2 style={L.h2}>Three steps, no timeline-wrangling</h2>
      <div style={L.steps}>
        {[
          ["1", "Say what to teach", "A topic, a pasted script, or a chat — in English, Español, Français, 中文, 日本語, or 한국어."],
          ["2", "The AI directs it", "Hook → build → payoff. Every beat gets the right visual: diagrams that move, characters that act, images in your art style."],
          ["3", "Edit & export", "Change any line, image, or scene by asking. Then export MP4 — or a green-screen subtitle overlay for your own editor."],
        ].map(([n, t, d]) => (
          <div key={n} style={L.step}>
            <div style={L.stepNum}>{n}</div>
            <h3 style={L.h3}>{t}</h3>
            <p style={L.p}>{d}</p>
          </div>
        ))}
      </div>
    </section>

    {/* ── What makes it different ── */}
    <section style={{ ...L.section, background: "#0f1216" }}>
      <h2 style={L.h2}>Animation, not a slideshow</h2>
      <p style={{ ...L.p, maxWidth: 640, margin: "0 auto 28px", textAlign: "center" }}>
        A motion engine built on the classical principles of animation — anticipation, squash &
        stretch, follow-through, parallax depth — draws every frame deterministically. That's why a
        video costs cents to make and looks like a studio made it.
      </p>
      <div style={L.grid}>
        {[
          ["🖐 Whiteboard doodle", "A marker hand sketches every scene onto the board."],
          ["🎭 Characters that act", "A cast walks in, gestures, and performs your story."],
          ["📖 Storybook stills", "Illustrated scenes with subtitles and voiceover."],
          ["🟩 Overlay exports", "Voice + subtitles on chroma green — key it over anything."],
          ["✨ Real editing feel", "Cuts whoosh, visuals pop, cameras settle — automatically."],
          ["🧠 Learning-first", "Guardrailed to teaching. No homework answers, no junk."],
        ].map(([t, d]) => (
          <div key={t} style={L.cell}>
            <h3 style={L.h3}>{t}</h3>
            <p style={L.p}>{d}</p>
          </div>
        ))}
      </div>
    </section>

    {/* ── Pricing ── */}
    <section id="pricing" style={L.section}>
      <h2 style={L.h2}>Two plans. No decoder ring.</h2>
      <div style={L.plans}>
        <div style={L.plan}>
          <h3 style={L.planName}>Free trial</h3>
          <div style={L.price}>$0</div>
          <p style={L.p}>One full-quality video (up to 2 min). Every theme, voice, and style.</p>
          <button className="v4-btn-primary" style={L.planCta} onClick={onStart}>Try it now</button>
        </div>
        <div style={{ ...L.plan, borderColor: ACCENT }}>
          <h3 style={L.planName}>Starter</h3>
          <div style={L.price}>$5.99<span style={L.per}>/month</span></div>
          <p style={L.p}><b>75 video-minutes a month.</b> Premium multilingual voice. Up to 5 videos generating at once.</p>
          <button className="v4-btn-primary" style={L.planCta} onClick={onStart} title="Payments launch shortly — the studio is open today">Start creating</button>
        </div>
        <div style={L.plan}>
          <h3 style={L.planName}>Unlimited</h3>
          <div style={L.price}>$499<span style={L.per}>/year</span></div>
          <p style={L.p}><b>Fair use: 500 minutes a month.</b> Everything in Starter, for people who ship daily.</p>
          <button className="v4-btn" style={{ ...L.planCta, background: "#171b20", color: "#e6eaef", border: "1px solid #333b44" }} onClick={onStart}>Start creating</button>
        </div>
      </div>
      <p style={L.finePrint}>
        Need more? Top up like mobile data — $20 → 200 min · $40 → 450 min · $80 → 1,000 min, valid
        12 months, no subscription. Other tools charge $0.15–$3 per finished minute; Starter works
        out to $0.08 — with the AI doing the directing.
      </p>
    </section>

    <footer style={L.footer}>
      <span>© 2026 Anivaze</span>
      <span style={{ display: "flex", gap: 16 }}>
        <a href="#terms" style={L.navLink}>Terms</a>
        <a href="#privacy" style={L.navLink}>Privacy</a>
      </span>
    </footer>
  </div>
);

/* ── Legal pages: a tiny markdown renderer (headings, lists, bold, quotes) ── */
function md(text: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  let list: string[] = [];
  let key = 0;
  const flush = () => {
    if (list.length) out.push(<ul key={`ul${key++}`} style={L.mdUl}>{list.map((li, i) => <li key={i} style={L.mdLi}>{inline(li)}</li>)}</ul>);
    list = [];
  };
  const inline = (s: string): React.ReactNode => {
    const parts = s.split(/\*\*(.+?)\*\*/g);
    return parts.map((p, i) => (i % 2 ? <b key={i}>{p}</b> : p));
  };
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (t.startsWith("- ") || /^\d+\.\s/.test(t)) { list.push(t.replace(/^-\s|^\d+\.\s/, "")); continue; }
    flush();
    if (!t) continue;
    if (t === "---") out.push(<hr key={key++} style={L.mdHr} />);
    else if (t.startsWith("### ")) out.push(<h3 key={key++} style={{ ...L.h3, marginTop: 24 }}>{inline(t.slice(4))}</h3>);
    else if (t.startsWith("## ")) out.push(<h2 key={key++} style={L.mdH2}>{inline(t.slice(3))}</h2>);
    else if (t.startsWith("# ")) out.push(<h1 key={key++} style={L.mdH1}>{inline(t.slice(2))}</h1>);
    else if (t.startsWith(">")) out.push(<blockquote key={key++} style={L.mdQuote}>{inline(t.replace(/^>+\s?/, ""))}</blockquote>);
    else out.push(<p key={key++} style={{ ...L.p, margin: "10px 0" }}>{inline(t)}</p>);
  }
  flush();
  return out;
}

export const LegalPage: React.FC<{ doc: "terms" | "privacy"; onBack: () => void }> = ({ doc, onBack }) => (
  <div style={L.root}>
    <header style={L.nav}>
      <div style={L.brand}><span style={L.dot} /> Anivaze</div>
      <button className="v4-btn" style={L.navCta} onClick={onBack}>← Back</button>
    </header>
    <main style={L.legal}>{md(doc === "terms" ? termsRaw : privacyRaw)}</main>
  </div>
);

const L: Record<string, React.CSSProperties> = {
  root: { minHeight: "100vh", background: "#0c0e11", color: "#e6eaef", fontFamily: "Inter, system-ui, sans-serif" },
  nav: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px clamp(16px, 5vw, 48px)", borderBottom: "1px solid #1e232a" },
  brand: { display: "flex", alignItems: "center", gap: 10, fontWeight: 800, fontSize: 18 },
  dot: { width: 10, height: 10, borderRadius: 999, background: ACCENT, boxShadow: `0 0 0 4px ${ACCENT}2e` },
  navLink: { color: "#9aa4b0", textDecoration: "none", fontSize: 14, minHeight: 40, display: "inline-flex", alignItems: "center" },
  navCta: { background: ACCENT, color: "#12151a", border: "none", borderRadius: 10, padding: "9px 16px", fontWeight: 700, fontSize: 14, cursor: "pointer", minHeight: 40 },
  hero: { textAlign: "center", padding: "clamp(48px, 10vh, 110px) 20px 36px", maxWidth: 780, margin: "0 auto" },
  h1: { fontSize: "clamp(2.2rem, 6vw, 3.6rem)", lineHeight: 1.08, letterSpacing: "-0.03em", fontWeight: 800, margin: "0 0 18px" },
  h1em: { fontStyle: "normal", color: ACCENT },
  sub: { fontSize: "clamp(1rem, 2.2vw, 1.15rem)", lineHeight: 1.55, color: "#a8b2bd", maxWidth: 620, margin: "0 auto 28px" },
  bigCta: { background: ACCENT, color: "#12151a", border: "none", borderRadius: 12, padding: "14px 24px", fontWeight: 800, fontSize: 16, cursor: "pointer", minHeight: 48 },
  heroNote: { fontSize: 12.5, color: "#8a939e", marginTop: 14 },
  strip: { display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap", padding: "8px 16px 40px" },
  stripItem: { fontSize: 12.5, color: "#9aa4b0", border: "1px solid #262c34", borderRadius: 999, padding: "6px 12px" },
  section: { padding: "56px clamp(16px, 5vw, 48px)", maxWidth: 1040, margin: "0 auto" },
  h2: { fontSize: "clamp(1.5rem, 3.5vw, 2rem)", letterSpacing: "-0.02em", fontWeight: 800, textAlign: "center", margin: "0 0 32px" },
  h3: { fontSize: 15.5, fontWeight: 700, margin: "0 0 6px" },
  p: { fontSize: 14, lineHeight: 1.55, color: "#a8b2bd", margin: 0 },
  steps: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 20 },
  step: { padding: "20px 18px", background: "#111418", border: "1px solid #1e232a", borderRadius: 14 },
  stepNum: { width: 30, height: 30, borderRadius: 999, background: ACCENT, color: "#12151a", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 12 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16, maxWidth: 1040, margin: "0 auto" },
  cell: { padding: "16px 18px", borderLeft: "1px solid #262c34" },
  plans: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 18 },
  plan: { padding: 22, background: "#111418", border: "1px solid #262c34", borderRadius: 16, display: "flex", flexDirection: "column", gap: 10 },
  planName: { fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#9aa4b0", margin: 0 },
  price: { fontSize: 34, fontWeight: 800, letterSpacing: "-0.02em" },
  per: { fontSize: 14, fontWeight: 500, color: "#8a939e" },
  planCta: { background: ACCENT, color: "#12151a", border: "none", borderRadius: 10, padding: "11px 16px", fontWeight: 700, fontSize: 14, cursor: "pointer", marginTop: "auto", minHeight: 44 },
  finePrint: { fontSize: 12.5, color: "#8a939e", textAlign: "center", marginTop: 22 },
  footer: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "24px clamp(16px, 5vw, 48px)", borderTop: "1px solid #1e232a", color: "#8a939e", fontSize: 13, flexWrap: "wrap", gap: 10 },
  legal: { maxWidth: 760, margin: "0 auto", padding: "40px 20px 80px" },
  mdH1: { fontSize: "1.7rem", fontWeight: 800, letterSpacing: "-0.02em", margin: "0 0 16px" },
  mdH2: { fontSize: "1.15rem", fontWeight: 700, margin: "28px 0 10px" },
  mdUl: { margin: "10px 0", paddingLeft: 22 },
  mdLi: { fontSize: 14, lineHeight: 1.6, color: "#a8b2bd", marginBottom: 6 },
  mdQuote: { borderLeft: "1px solid #3a424c", margin: "12px 0", padding: "4px 14px", color: "#c8cfd7", fontSize: 14, lineHeight: 1.55, background: "#111418" },
  mdHr: { border: "none", borderTop: "1px solid #262c34", margin: "24px 0" },
};
